import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import BrandLogo from "../../components/common/BrandLogo";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useAppDispatch, useAppSelector } from "../../store";
import { forgotPassword, clearError } from "../../features/auth/authSlice";
import "../../styles/auth.scss";
import Swal from "sweetalert2";

interface ForgotPasswordFormInputs {
    email: string;
}

const schema = yup.object().shape({
    email: yup
        .string()
        .required("Email is required")
        .email("Must be a valid email"),
});

const ForgotPasswordPage: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { isLoading, error } = useAppSelector((state) => state.auth);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<ForgotPasswordFormInputs>({
        resolver: yupResolver(schema),
    });

    // Component unmount olduğunda hata mesajını temizle
    React.useEffect(() => {
        return () => {
            dispatch(clearError());
        };
    }, [dispatch]);

    const onSubmit = async (data: ForgotPasswordFormInputs) => {
        const resultAction = await dispatch(forgotPassword(data.email));
        if (forgotPassword.fulfilled.match(resultAction)) {
            Swal.fire({
                icon: "success",
                title: "Reset Code Sent!",
                html: `<p style="margin: 0; font-size: 15px; color: #64748b;">We've sent a password reset code to</p><p style="margin: 8px 0 0 0; font-size: 15px; color: #1e293b; font-weight: 600;">${data.email}</p>`,
                timer: 3000,
                showConfirmButton: false,
                customClass: {
                    popup: 'swal-modern-popup',
                    title: 'swal-modern-title',
                    htmlContainer: 'swal-modern-html'
                }
            }).then(() => {
                navigate("/reset-password", { state: { email: data.email } });
            });
        }
    };

    return (
        <div className="auth-page forgot-password-page">
            <div className="gradient-orb"></div>
            <div className="auth-container">
                <div className="auth-card-wrapper">
                    <div className="auth-card">
                        <div className="auth-logo">
                            <BrandLogo height={28} />
                        </div>

                        <h2 className="form-title">Forgot Password</h2>
                        <p className="form-subtitle">
                            Enter your email address and we'll send you a code to reset your password.
                        </p>

                        {error && (
                            <div className="alert" role="alert">
                                {error}
                            </div>
                        )}

                        <form className="auth-form" onSubmit={handleSubmit(onSubmit)}>
                            <div className="form-group">
                                <div className="input-group">
                                    <input
                                        type="email"
                                        className={`form-control ${errors.email ? "is-invalid" : ""} pl-3`}
                                        id="email"
                                        placeholder="Email address"
                                        {...register("email")}
                                    />
                                    {errors.email && (
                                        <div className="error-message">{errors.email.message}</div>
                                    )}
                                </div>
                            </div>

                            <button
                                type="submit"
                                className={`auth-button ${isLoading ? "loading" : ""}`}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <span className="spinner" role="status" aria-hidden="true"></span>
                                        Sending Code...
                                    </>
                                ) : (
                                    "Send Reset Code"
                                )}
                            </button>

                            <div className="register-prompt">
                                <p>
                                    Remember your password?{" "}
                                    <Link to="/login" className="login-link">
                                        <span>Sign in</span>
                                    </Link>
                                </p>
                            </div>
                        </form>
                    </div>

                    <div className="copyright">
                        <div className="copyright-content">
                            <span>&copy; {new Date().getFullYear()} Meylo</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForgotPasswordPage;
